import { lazy, Suspense, useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { RealtimeSync } from '../../features/realtime/RealtimeSync';
import { Dashboard } from '../../features/dashboard/Dashboard';
import { useDashboardNavigation } from '../../features/dashboard/useDashboardNavigation';
import { PwaExperience } from '../../pwa/PwaExperience';
import { Dock } from './Dock';
import { Header } from './Header';
import { LocalDemoBanner } from './LocalDemoBanner';
import { Sidebar } from './Sidebar';

const BillingPage = lazy(async () => ({
  default: (await import('../../features/billing/BillingPage')).BillingPage,
}));
const AccountPage = lazy(async () => ({
  default: (await import('../../features/account/AccountPage')).AccountPage,
}));
const BanksPage = lazy(async () => ({
  default: (await import('../../features/plaid/BanksPage')).BanksPage,
}));
const NotificationsPage = lazy(async () => ({
  default: (await import('../../features/notifications/NotificationsPage')).NotificationsPage,
}));
const TicketsPage = lazy(async () => ({
  default: (await import('../../features/tickets/TicketsPage')).TicketsPage,
}));
const AdminPage = lazy(async () => ({
  default: (await import('../../features/admin/AdminPage')).AdminPage,
}));
const WalletsPage = lazy(async () => ({
  default: (await import('../../features/wallets/WalletsPage')).WalletsPage,
}));

const routeTargetIds = [
  'admin',
  'agenda',
  'ajustes',
  'bancos',
  'calendario',
  'cuenta',
  'herramientas',
  'inicio',
  'notificaciones',
  'plan',
  'rapido',
  'referidos',
  'soporte',
  'upgrade',
  'wallets',
] as const;

export function AppShell() {
  const { t } = useI18n();
  const {
    navigate,
    navigateAdmin,
    navigateAccount,
    navigateBanks,
    navigateNotifications,
    navigateSupport,
    navigateUpgrade,
    navigateWallets,
    route,
    view,
  } = useDashboardNavigation();
  const [addMovementOpen, setAddMovementOpen] = useState(false);
  const [planFiltersOpen, setPlanFiltersOpen] = useState(false);
  const [walletAddOpen, setWalletAddOpen] = useState(false);
  const [walletFiltersOpen, setWalletFiltersOpen] = useState(false);
  const [toolsFiltersOpen, setToolsFiltersOpen] = useState(false);
  const [lastAddedTransactionId, setLastAddedTransactionId] = useState<string>();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        {t('common.skipContent')}
      </a>
      <PwaExperience />
      <LocalDemoBanner />
      <Header
        notificationsActive={route === 'notifications'}
        onNotifications={navigateNotifications}
        onSettings={() => navigateAccount('preferences')}
      />
      <RealtimeSync />
      <Sidebar
        accountActive={route === 'account'}
        adminActive={route === 'admin'}
        banksActive={route === 'banks'}
        billingActive={route === 'upgrade'}
        onAccount={navigateAccount}
        onAdmin={navigateAdmin}
        onBanks={navigateBanks}
        onNavigate={navigate}
        onUpgrade={navigateUpgrade}
        onWallets={navigateWallets}
        onSupport={navigateSupport}
        referralsActive={route === 'referrals'}
        supportActive={route === 'tickets'}
        walletsActive={route === 'wallets'}
        view={view}
      />
      {routeTargetIds.map((id) => (
        <span className="route-focus-target" id={id} key={id} tabIndex={-1} />
      ))}
      {route === 'upgrade' ? (
        <Suspense fallback={<BillingLoadingFallback />}>
          <BillingPage onBack={() => navigate('home')} />
        </Suspense>
      ) : route === 'banks' ? (
        <Suspense fallback={<PageLoadingFallback label={t('common.loadingBanks')} />}>
          <BanksPage onBack={() => navigateAccount('sharing')} />
        </Suspense>
      ) : route === 'notifications' ? (
        <Suspense fallback={<PageLoadingFallback label={t('common.loadingNotifications')} />}>
          <NotificationsPage onBack={() => navigate('home')} />
        </Suspense>
      ) : route === 'tickets' ? (
        <Suspense fallback={<PageLoadingFallback label={t('common.loadingSupport')} />}>
          <TicketsPage onBack={() => navigateAccount('sharing')} />
        </Suspense>
      ) : route === 'admin' ? (
        <Suspense fallback={<PageLoadingFallback label={t('common.loadingAdmin')} />}>
          <AdminPage onBack={() => navigate('home')} />
        </Suspense>
      ) : route === 'wallets' ? (
        <Suspense fallback={<PageLoadingFallback label={t('navigation.wallets')} />}>
          <WalletsPage
            addOpen={walletAddOpen}
            filtersOpen={walletFiltersOpen}
            onCloseAdd={() => setWalletAddOpen(false)}
            onCloseFilters={() => setWalletFiltersOpen(false)}
          />
        </Suspense>
      ) : route === 'account' || route === 'referrals' || route === 'settings' ? (
        <Suspense fallback={<AccountLoadingFallback label={t('common.loadingAccount')} />}>
          <AccountPage
            initialTab={
              route === 'referrals' ? 'referrals' : route === 'settings' ? 'preferences' : 'sharing'
            }
            key={route}
            onBack={() => navigate('home')}
            onBanks={navigateBanks}
            onAdmin={navigateAdmin}
            onSupport={navigateSupport}
            onUpgrade={navigateUpgrade}
          />
        </Suspense>
      ) : (
        <Dashboard
          addMovementOpen={addMovementOpen}
          filtersOpen={planFiltersOpen}
          lastAddedTransactionId={lastAddedTransactionId}
          onCloseAddMovement={() => setAddMovementOpen(false)}
          onCloseFilters={() => setPlanFiltersOpen(false)}
          onOpenAddMovement={() => setAddMovementOpen(true)}
          onOpenFilters={() => setPlanFiltersOpen(true)}
          onOpenTools={() => {
            setPlanFiltersOpen(false);
            navigate('tools');
          }}
          onCloseToolsFilters={() => setToolsFiltersOpen(false)}
          onTransactionCreated={setLastAddedTransactionId}
          toolsFiltersOpen={toolsFiltersOpen}
          onUpgrade={navigateUpgrade}
          view={view}
        />
      )}
      <Dock
        activeDestination={getActiveDockDestination(route)}
        context={getDockContext(route)}
        onAdd={() => {
          if (route === 'wallets') setWalletAddOpen(true);
          else setAddMovementOpen(true);
        }}
        onBack={() => {
          setPlanFiltersOpen(false);
          setWalletAddOpen(false);
          setToolsFiltersOpen(false);
          setWalletFiltersOpen(false);
          navigate(getBackDestination(route));
        }}
        onFilter={() => {
          if (route === 'wallets') setWalletFiltersOpen(true);
          else if (route === 'tools') setToolsFiltersOpen(true);
          else if (route === 'plan') setPlanFiltersOpen(true);
        }}
        onNavigate={navigate}
        onSettings={() => navigateAccount('preferences')}
        showFilter={route === 'plan' || route === 'tools' || route === 'wallets'}
        onWallets={navigateWallets}
      />
    </div>
  );
}

function getDockContext(route: ReturnType<typeof useDashboardNavigation>['route']) {
  if (route === 'plan' || route === 'wallets') return 'plan' as const;
  if (route === 'agenda' || route === 'calendar' || route === 'tools') return 'tools' as const;
  return 'main' as const;
}

function getActiveDockDestination(route: ReturnType<typeof useDashboardNavigation>['route']) {
  return route;
}

function getBackDestination(
  route: ReturnType<typeof useDashboardNavigation>['route'],
): 'home' | 'plan' | 'tools' {
  if (route === 'wallets') return 'plan';
  if (route === 'agenda' || route === 'calendar') return 'tools';
  return 'home';
}

function AccountLoadingFallback({ label }: { readonly label: string }) {
  return (
    <main
      aria-busy="true"
      aria-label={label}
      className="account-loading app-content"
      id="main-content"
    />
  );
}

function PageLoadingFallback({ label }: { readonly label: string }) {
  return (
    <main
      aria-busy="true"
      aria-label={label}
      className="account-loading app-content"
      id="main-content"
    />
  );
}

function BillingLoadingFallback() {
  const { t } = useI18n();
  return (
    <main
      aria-busy="true"
      aria-label={t('common.loadingPlans')}
      className="app-content billing-skeleton"
      id="main-content"
    >
      <span />
      <span />
      <span />
    </main>
  );
}
